import React from "react";
import styles from "../../styles";
import { FaInfoCircle } from "react-icons/fa";
import type { BrandFeatureInfo } from "./BrandFeaturesModal";

const cabinClassOptions = [
  { label: "Economy", code: "Y" },
  { label: "Premium Economy", code: "S" },
  { label: "Business", code: "C" },
  { label: "Premium Business", code: "J" },
  { label: "First class", code: "F" },
  { label: "Premium First", code: "P" },
];

function getCabinLabel(code?: string) {
  return (
    cabinClassOptions.find((c) => c.code === code)?.label || code || "Unknown"
  );
}

interface FareCardProps {
  label: string;
  price: number;
  currency: string;
  baggage: string;
  cancellation: string;
  addOns?: string;
  cabinCode?: string;
  selected?: boolean;
  brandFeatures?: BrandFeatureInfo[];
  /** When provided (e.g. by FlippableFareCard), info icon click calls this instead of opening a modal */
  onInfoClick?: () => void;
}

export default function FareCard({
  label,
  price,
  currency,
  baggage,
  cancellation,
  cabinCode,
  selected = false,
  brandFeatures,
  onInfoClick,
}: FareCardProps) {
  const hasBrandFeatures =
    Array.isArray(brandFeatures) && brandFeatures.length > 0;

  return (
    <div
      className={`w-full p-3 sm:p-4 rounded-xl shadow-md transition-all duration-200 ${
        selected ? "bg-primary text-white" : "bg-white text-[#0B2F9F]"
      } ${styles.flexCol}`}
    >
      <div className="flex justify-between items-center border-b mb-1.5 sm:mb-2 pb-1 sm:pb-1.5">
        <p className="font-bold text-xs sm:text-sm">{label}</p>
        {hasBrandFeatures && (
          <button
            type="button"
            data-info-icon
            onClick={(e) => {
              e.stopPropagation();
              onInfoClick?.();
            }}
            className={`p-1 rounded-full hover:opacity-80 transition-opacity ${
              selected ? "text-white" : "text-primary"
            }`}
            aria-label="View brand features"
          >
            <FaInfoCircle className="w-4 h-4" />
          </button>
        )}
      </div>
      <div className="flex-1 space-y-0 min-h-0">
        <div
          className={`py-1 sm:py-1.5 border-b ${selected ? "border-white/30" : "border-gray-200"} flex flex-wrap items-baseline gap-x-1.5`}
        >
          <span
            className={`text-2xs sm:text-xs font-medium shrink-0 ${selected ? "text-white/90" : "text-gray-500"}`}
          >
            Fare:
          </span>
          <span
            className={`text-xs sm:text-sm break-words font-medium ${selected ? "text-white" : "text-[#0B2F9F]"}`}
          >
            {currency} {price}
          </span>
        </div>
        <div
          className={`py-1 sm:py-1.5 border-b ${selected ? "border-white/30" : "border-gray-200"} flex flex-wrap items-baseline gap-x-1.5`}
        >
          <span
            className={`text-2xs sm:text-xs font-medium shrink-0 ${selected ? "text-white/90" : "text-gray-500"}`}
          >
            Baggage:
          </span>
          <span
            className={`text-xs sm:text-sm break-words font-medium ${selected ? "text-white" : "text-[#0B2F9F]"}`}
          >
            {baggage}
          </span>
        </div>
        <div
          className={`py-1 sm:py-1.5 border-b ${selected ? "border-white/30" : "border-gray-200"} flex flex-wrap items-baseline gap-x-1.5`}
        >
          <span
            className={`text-2xs sm:text-xs font-medium shrink-0 ${selected ? "text-white/90" : "text-gray-500"}`}
          >
            Cancellation:
          </span>
          <span
            className={`text-xs sm:text-sm break-words font-medium ${selected ? "text-white" : "text-[#0B2F9F]"}`}
          >
            {cancellation}
          </span>
        </div>
        <div className="py-1 sm:py-1.5 flex flex-wrap items-baseline gap-x-1.5">
          <span
            className={`text-2xs sm:text-xs font-medium shrink-0 ${selected ? "text-white/90" : "text-gray-500"}`}
          >
            Class:
          </span>
          <span
            className={`text-xs sm:text-sm break-words font-medium ${selected ? "text-white" : "text-[#0B2F9F]"}`}
          >
            {getCabinLabel(cabinCode)}
          </span>
        </div>
      </div>
    </div>
  );
}
